import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  PackagePlus, ClipboardList, SlidersHorizontal, ArrowRightLeft, FileText,
  Truck, User, Clock, MapPin, AlertTriangle
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('ro-RO', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  } catch { return dateStr; }
};

const formatCurrency = (num) => {
  if (num === undefined || num === null) return '-';
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(num);
};

const TITLE_MAP = {
  receipt: { icon: PackagePlus, color: 'text-green-600', label: 'Detalii Receptie' },
  quick_receipt: { icon: PackagePlus, color: 'text-orange-600', label: 'Detalii Receptie Rapida' },
  ticket: { icon: ClipboardList, color: 'text-blue-600', label: 'Detalii Tichet' },
  adjustment: { icon: SlidersHorizontal, color: 'text-amber-600', label: 'Detalii Ajustare' },
  transfer: { icon: ArrowRightLeft, color: 'text-purple-600', label: 'Detalii Transfer' },
};

export const DocumentDetailModal = ({ detailModal, onClose }) => {
  const titleCfg = TITLE_MAP[detailModal.type] || { icon: FileText, color: 'text-slate-600', label: 'Detalii Document' };
  const Icon = titleCfg.icon;

  return (
    <Dialog open={detailModal.open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${titleCfg.color}`} /> {titleCfg.label}
          </DialogTitle>
        </DialogHeader>

        {detailModal.loading ? (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            <p className="text-slate-500">Se incarca detaliile...</p>
          </div>
        ) : detailModal.data?.error ? (
          <div className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-600">{detailModal.data.message}</p>
          </div>
        ) : detailModal.data ? (
          <div className="space-y-4">
            {(detailModal.type === 'receipt' || detailModal.type === 'quick_receipt') && (
              <ReceiptDetails data={detailModal.data} />
            )}
            {detailModal.type === 'ticket' && <TicketDetails data={detailModal.data} />}
            {detailModal.type === 'adjustment' && <AdjustmentDetails data={detailModal.data} />}
            {detailModal.type === 'transfer' && <TransferDetails data={detailModal.data} />}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Inchide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReceiptDetails = ({ data }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Nr. Receptie" value={data.receipt_number || '-'} mono />
      <Field label="Tip">
        <Badge variant={data.receipt_type === 'quick' ? 'warning' : 'default'}>
          {data.receipt_type === 'quick' ? 'Receptie Rapida' : 'Receptie cu Factura'}
        </Badge>
      </Field>
      <Field label="Nr. Factura" value={data.invoice_number || '-'} mono />
      <Field label="Data Factura" value={formatDate(data.invoice_date || data.created_at)?.split(',')[0]} />
      <Field label="Furnizor" icon={Truck}>{data.supplier_name || '-'}</Field>
      <Field label="Creat de" icon={User}>{data.created_by_name || '-'}</Field>
      <Field label="Cantitate Totala"><span className="font-semibold text-green-600">+{data.total_quantity || 0}</span></Field>
      <Field label="Valoare Totala"><span className="font-semibold">{formatCurrency(data.total_invoice_value || 0)}</span></Field>
    </div>
    {data.items?.length > 0 && (
      <div className="border-t pt-4">
        <Label className="text-sm font-medium mb-2 block">Articole ({data.items.length})</Label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {data.items.map((item, idx) => (
            <div key={idx} className="bg-slate-50 p-2 rounded-lg flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{item.article_name}</p>
                <p className="text-xs text-slate-500 font-mono">{item.article_code}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold">{item.quantity} {item.unit}</p>
                {item.unit_price > 0 && <p className="text-xs text-slate-500">{formatCurrency(item.unit_price)}/buc</p>}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    {data.notes && (
      <div className="border-t pt-4">
        <Label className="text-xs text-slate-500">Note</Label>
        <p className="text-sm mt-1 bg-slate-50 p-2 rounded">{data.notes}</p>
      </div>
    )}
  </>
);

const TicketDetails = ({ data }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Nr. Tichet" value={data.number || '-'} mono />
      <Field label="Status">
        <Badge variant={data.status === 'closed' ? 'success' : data.status === 'in_progress' ? 'warning' : 'default'}>
          {data.status === 'closed' ? 'Inchis' : data.status === 'in_progress' ? 'In Lucru' : data.status}
        </Badge>
      </Field>
      <div className="col-span-2 space-y-1">
        <Label className="text-xs text-slate-500">Titlu</Label>
        <p className="font-medium">{data.title || '-'}</p>
      </div>
      <Field label="Prioritate">
        <Badge variant={data.priority === 'high' ? 'destructive' : data.priority === 'warning' ? 'warning' : 'default'}>
          {data.priority === 'high' ? 'Urgenta' : data.priority === 'medium' ? 'Medie' : 'Normala'}
        </Badge>
      </Field>
      <Field label="Echipament" value={data.equipment_name || '-'} />
      <Field label="Asignat la" icon={User}>{data.assigned_to_name || '-'}</Field>
      <Field label="Creat la" icon={Clock}>{formatDate(data.created_at)}</Field>
    </div>
    {data.materials?.length > 0 && (
      <div className="border-t pt-4">
        <Label className="text-sm font-medium mb-2 block">Materiale Folosite ({data.materials.length})</Label>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {data.materials.map((mat, idx) => (
            <div key={idx} className="bg-red-50 p-2 rounded-lg flex justify-between items-center">
              <div>
                <p className="font-medium text-sm">{mat.article_name || mat.name}</p>
                <p className="text-xs text-slate-500 font-mono">{mat.article_code || mat.code}</p>
              </div>
              <p className="font-semibold text-red-600">-{mat.quantity_used || mat.quantity}</p>
            </div>
          ))}
        </div>
      </div>
    )}
    {data.description && (
      <div className="border-t pt-4">
        <Label className="text-xs text-slate-500">Descriere</Label>
        <p className="text-sm mt-1 bg-slate-50 p-2 rounded">{data.description}</p>
      </div>
    )}
  </>
);

const AdjustmentDetails = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="Tip Ajustare" value={data.adjustment_type || data.type || '-'} />
    <Field label="Motiv" value={data.reason || '-'} />
    <Field label="Cantitate Veche" value={data.old_quantity ?? '-'} mono />
    <Field label="Cantitate Noua" value={data.new_quantity ?? '-'} mono />
    <Field label="Diferenta">
      <span className={`font-semibold ${(data.quantity_diff || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
        {(data.quantity_diff || 0) > 0 ? '+' : ''}{data.quantity_diff || 0}
      </span>
    </Field>
    <Field label="Efectuat de" icon={User}>{data.created_by_name || '-'}</Field>
    <div className="col-span-2 space-y-1">
      <Label className="text-xs text-slate-500">Data</Label>
      <p className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-slate-400" /> {formatDate(data.created_at)}</p>
    </div>
    {data.notes && (
      <div className="col-span-2 space-y-1">
        <Label className="text-xs text-slate-500">Note</Label>
        <p className="text-sm bg-slate-50 p-2 rounded">{data.notes}</p>
      </div>
    )}
  </div>
);

const TransferDetails = ({ data }) => (
  <div className="grid grid-cols-2 gap-4">
    <Field label="ID Transfer" value={data.id || '-'} mono />
    <Field label="Cantitate"><span className="font-semibold text-blue-600">{data.quantity || '-'}</span></Field>
    <Field label="De la BIN" icon={MapPin} iconColor="text-red-400">{data.from_bin || '-'}</Field>
    <Field label="La BIN" icon={MapPin} iconColor="text-green-400">{data.to_bin || '-'}</Field>
    {data.note && (
      <div className="col-span-2 space-y-1">
        <Label className="text-xs text-slate-500">Note</Label>
        <p className="text-sm bg-slate-50 p-2 rounded">{data.note}</p>
      </div>
    )}
  </div>
);

const Field = ({ label, value, mono, icon: FieldIcon, iconColor = 'text-slate-400', children }) => (
  <div className="space-y-1">
    <Label className="text-xs text-slate-500">{label}</Label>
    {children ? (
      <div className="flex items-center gap-1">
        {FieldIcon && <FieldIcon className={`w-3.5 h-3.5 ${iconColor}`} />}
        {children}
      </div>
    ) : (
      <p className={mono ? 'font-mono font-semibold' : ''}>{value}</p>
    )}
  </div>
);
